import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Loader,
  Zap,
  BarChart3,
  Compass,
  Lightbulb,
  Rocket,
  Target,
  Briefcase,
  TrendingUp,
  Settings,
  Globe,
  Database,
  Layout,
  PieChart,
  AlertCircle,
  CheckCircle,
  Cloud,
  FileText,
  ExternalLink,
} from "lucide-react";
import backgroundImage from "../../app/assets/videos/backgroundImage.png";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import Login from "../../app/auth/Login";
import Header from "../../app/nav/Header";
import { getToken } from "../../app/auth/authConfig";
import axios from "axios";
import FileViewModal from "../../components/modal/FileViewModal";
import ReactMarkdown from "react-markdown";

const ApplicationHub = () => {
  const [expandedSection, setExpandedSection] = useState("");
  const [data, setData] = useState([]);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingCardId, setLoadingCardId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFileViewModal, setShowFileViewModal] = useState(false);
  const [fileViewData, setFileViewData] = useState(null);
  const navigate = useNavigate();

  const handleViewDetails = async (toolId) => {
    setLoadingCardId(`details-${toolId}`);
    setTimeout(() => {
      navigate(`/details/${toolId}`);
      setLoadingCardId(null);
    }, 250);
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/with-access`;
      const token = await getToken();
      const res = await axios.get(baseURL, {
        headers: { "oidc-token": token },
      });
      setData(res.data);
      if (res.data.length > 0) {
        setExpandedSection(res.data[0].category);
      }
    } catch (err) {
      console.error("Fetch error", err);
      setError("Failed to load applications");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      // If search is cleared, fetch all data again
      fetchData();
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/search?q=${encodeURIComponent(query)}`;
      const token = await getToken();
      const res = await axios.get(baseURL, {
        headers: { "oidc-token": token },
      });
      // Format search results to match the with-access endpoint structure
      const formattedResults = res.data.map((item) => ({
        ...item,
        title: item.toolName,
        category: item.tooltype,
        isAccess: true,
        isApproved: item.status,
        isEmbedded: false,
        isIframe: false,
        reportId: null,
      }));
      setData(formattedResults);
      if (formattedResults.length > 0) {
        setExpandedSection(formattedResults[0].category);
      }
    } catch (err) {
      console.error("Search error", err);
      setError("Failed to search applications");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const iconArray = [
    Activity,
    Zap,
    BarChart3,
    Compass,
    Lightbulb,
    Rocket,
    Target,
    Briefcase,
    TrendingUp,
    Settings,
    Globe,
    Database,
    Layout,
    PieChart,
    AlertCircle,
    CheckCircle,
    Cloud,
  ];

  const getIconForCard = (cardId) => {
    const IconComponent = iconArray[cardId % iconArray.length];
    return <IconComponent className="w-8 h-8" />;
  };

  const categoriesOptions = [
    ...new Set(data.map((t) => t?.category).filter(Boolean)),
  ];

  const buildCardsByCategory = (category) =>
    data
      .filter((item) => item.category === category)
      .map((item) => ({
        icon: getIconForCard(item.id),
        ...item,
      }));

  const handleCardClick = (card) => {
    if (card.isIframe) {
      setIframeUrl(card.url); // open popup with iframe
    } else if (card.isEmbedded) {
      navigate(`/tool/report/${card.reportId}`); // Power BI page
    } else {
      window.open(card.url, "_blank"); // normal navigation
    }
  };

  const renderCardsContent = () => {
    if (isLoading) {
      return Array.from({ length: 8 }).map((_, index) => (
        <div
          key={`skeleton-${index}`}
          className="bg-black border-2 border-[#E12AFB]/30 rounded-2xl p-6 animate-pulse flex flex-col"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="w-8 h-8 bg-gray-700 rounded" />
            <div className="h-5 w-20 bg-gray-700 rounded-full" />
          </div>
          <div className="h-4 bg-gray-700 rounded w-3/4 mb-2" />
          <div className="h-3 bg-gray-700 rounded w-full flex-grow mb-2" />
          <div className="h-3 bg-gray-700 rounded w-1/2 mb-4" />
          <div className="flex gap-2">
            <div className="flex-1 h-8 bg-gray-700 rounded-lg" />
            <div className="flex-1 h-8 bg-gray-700 rounded-lg" />
          </div>
        </div>
      ));
    }

    if (error) {
      return (
        <div className="col-span-full text-center py-8">
          <p className="text-red-500">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-4 py-2 bg-[#E12AFB] text-white rounded-lg hover:bg-[#E12AFB]/80"
          >
            Retry
          </button>
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div className="col-span-full text-center py-8">
          <p className="text-white/70">
            {searchQuery
              ? `No applications found for "${searchQuery}"`
              : "No applications available"}
          </p>
        </div>
      );
    }

    return buildCardsByCategory(expandedSection).map((card) => (
      <div
        key={card.id}
        className="rounded-2xl p-7 shadow-xl border-0 bg-gradient-to-br from-[#f8e1ff] via-[#f3f8ff] to-[#e1f7ff] hover:scale-[1.02] transition-transform flex flex-col relative overflow-hidden group"
        style={{ boxShadow: '0 8px 32px 0 rgba(225,42,251,0.10), 0 1.5px 8px 0 rgba(0,0,0,0.08)' }}
      >
        <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#fff]/10 rounded-full blur-2xl z-0 group-hover:scale-110 transition-transform" />
        <div className="flex items-center justify-between mb-3 z-10 relative">
          <div className="p-2 text-[#333]">{card.icon}</div>
          {card.isApproved === "approved" && (
            <div className="bg-green-500/90 text-white px-2 py-1 rounded-full text-[10px] font-semibold flex items-center gap-1 shadow">
              ✓ Verified
            </div>
          )}
        </div>
        <h3 className="text-[16px] font-semibold text-[#7d1fa3] mb-1 z-10 relative">{card.title}</h3>
        <div
          className="text-[12px] text-[#3a3a3a] flex-grow mt-1 leading-relaxed [&_p]:mb-1 [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_strong]:text-[#7d1fa3] [&_a]:text-blue-600 [&_a]:underline z-10 relative overflow-hidden"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            textOverflow: "ellipsis",
          }}
          title={card.description || ""}
        >
          {card.description || ""}
        </div>
          {/* Tags display */}
          {card.tags && (
            <div className="flex flex-wrap gap-1 mt-2 mb-1 z-10 relative">
              {String(card.tags)
                .split(",")
                .map((tag, idx) => tag.trim())
                .filter(Boolean)
                .map((tag, idx) => (
                  <span
                    key={tag + idx}
                    className="px-2 py-0.5 bg-[#E12AFB]/10 text-[#7d1fa3] text-[11px] rounded-full font-medium border border-[#E12AFB]/20"
                  >
                    {tag}
                  </span>
                ))}
            </div>
          )}
        {card.createdAt && (
          <p className="text-[10px] text-[#7d1fa3]/60 mt-2 z-10 relative">
            Created: {new Date(card.createdAt).toLocaleDateString()}
          </p>
        )}
        <div className="flex gap-2 mt-4 z-10 relative">
         
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(card.id);
            }}
            disabled={loadingCardId === `details-${card.id}`}
            title="View Details"
            className="flex-1 px-3 py-2 bg-[#E12AFB]/90 hover:bg-[#E12AFB] disabled:bg-[#E12AFB]/30 text-white rounded-lg transition-colors flex items-center justify-center disabled:cursor-not-allowed shadow"
          >
            {loadingCardId === `details-${card.id}` ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              "Details"
            )}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setLoadingCardId(`tool-${card.id}`);
              setTimeout(() => {
                handleCardClick(card);
                setLoadingCardId(null);
              }, 300);
            }}
            disabled={loadingCardId === `tool-${card.id}`}
            title="Go to Tool"
            className="flex-1 px-3 py-2 bg-gradient-to-br from-[#E12AFB] to-[#7d1fa3] hover:from-[#7d1fa3] hover:to-[#E12AFB] disabled:bg-[#E12AFB]/30 text-white rounded-lg transition-colors flex items-center justify-center disabled:cursor-not-allowed shadow"
          >
            {loadingCardId === `tool-${card.id}` ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
            <> Go To  <ExternalLink className="w-4 h-4 ml-2" /></>
            )}
          </button>
        </div>
      </div>
    ));
  };

  return (
    <>
      <AuthenticatedTemplate>
        <div className="w-full min-h-screen flex flex-col">
          <Header />

          <div className="flex flex-col flex-1 text-white">
            <div className="w-full flex justify-end items-center gap-6 p-4 bg-black">
              <input
                placeholder="Search Apps..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="ml-4 h-7 w-56 rounded-full bg-transparent px-4 text-[11px] placeholder-white/60 border border-[#fff]"
              />
            </div>

            <div
              className="bg-cover bg-center flex-1"
              style={{ backgroundImage: `url(${backgroundImage})` }}
            >
              <div className="w-full h-full flex flex-col">
                <div className="flex flex-col items-center mt-4">
                  <h1 className="text-[28px] font-medium">Application Hub</h1>
                  <p className="text-[28px] text-white/70 font-light">
                    Access all your tools in one place
                  </p>

                  <div className="flex gap-12 mt-2">
                    {isLoading ? (
                      // Loading Skeleton for Tabs
                      Array.from({ length: 3 }).map((_, index) => (
                        <div
                          key={index}
                          className="h-6 w-32 bg-gray-700/50 rounded animate-pulse"
                        />
                      ))
                    ) : (
                      // Actual Tabs
                      categoriesOptions.map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setExpandedSection(tab)}
                          className="text-[15px] relative pb-2"
                        >
                          {tab}
                          <div
                            className={`h-[3px] mt-1 w-[50%] mx-auto ${
                              expandedSection === tab
                                ? "bg-[#E12AFB]"
                                : "bg-transparent"
                            }`}
                          />
                        </button>
                      ))
                    )}
                  </div>
                </div>

                <div className="flex flex-col p-5 sm:p-6 mt-8 w-[96%] max-w-[1800px] mx-auto">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {renderCardsContent()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Iframe Popup */}
        {iframeUrl && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-white w-[80%] h-[80%] rounded-xl relative">
              <button
                className="absolute top-2 right-3 text-black"
                onClick={() => setIframeUrl(null)}
              >
                ✕
              </button>
              <iframe
                src={iframeUrl}
                title="Iframe Tool"
                className="w-full h-full rounded-xl"
                frameBorder="0"
              />
            </div>
          </div>
        )}

        {showFileViewModal && fileViewData && (
          <FileViewModal
            filePath={fileViewData.filePath}
            fileName={fileViewData.fileName}
            onClose={() => setShowFileViewModal(false)}
          />
        )}

      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>
    </>
  );
};

export default ApplicationHub;
